<template>
  <Head title="Kelola Widgets" />
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div class="space-y-6">
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            { name: 'Kelola Widgets', href: route('widgets.index') },
          ]"
        />

        <!-- Table Card -->
        <div class="bg-white border border-gray-200 shadow-sm rounded-xl">
          <!-- Card Header -->
          <div class="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-xl">
            <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h2 class="flex items-center gap-2 text-lg font-bold text-white">
                <i class="fa-solid fa-puzzle-piece"></i>
                Daftar Widget
                <span class="ml-1 px-2 py-0.5 text-xs font-medium text-indigo-100 bg-white/20 rounded-full">
                  {{ allWidgets.length }}
                </span>
              </h2>
              <!-- Search -->
              <div class="relative w-full sm:w-64">
                <i class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-white/60 text-xs pointer-events-none"></i>
                <input
                  v-model="search"
                  type="text"
                  autocomplete="off"
                  placeholder="Cari widget..."
                  class="w-full pl-8 pr-4 py-2 text-sm bg-white/20 text-white placeholder-white/60 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50 focus:bg-white/30"
                />
              </div>
            </div>
          </div>

          <!-- Drag-drop hint -->
          <div class="px-5 py-2.5 border-b border-indigo-100 bg-indigo-50 flex items-center gap-2 text-xs text-indigo-600">
            <i class="fa-solid fa-circle-info"></i>
            Seret baris menggunakan ikon <i class="fa-solid fa-grip-vertical mx-1"></i> untuk mengubah urutan. Perubahan otomatis disimpan.
          </div>

          <!-- Table -->
          <div class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200">
              <thead class="bg-gray-50">
                <tr>
                  <th class="py-3 pl-4 pr-2 w-10 text-left text-xs font-semibold uppercase tracking-wide text-gray-500"></th>
                  <th class="py-3 px-3 w-8 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">#</th>
                  <th class="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Nama Widget</th>
                  <th class="hidden md:table-cell py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Komponen</th>
                  <th class="hidden lg:table-cell py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Deskripsi</th>
                  <th class="py-3 px-4 text-center text-xs font-semibold uppercase tracking-wide text-gray-500 w-16">Urutan</th>
                  <th class="py-3 px-4 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 w-24">Status</th>
                  <th class="py-3 pl-3 pr-6 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Aksi</th>
                </tr>
              </thead>
              <tbody ref="widgetList" class="bg-white divide-y divide-gray-100">
                <tr v-if="!filteredWidgets.length">
                  <td colspan="8" class="py-12 text-center text-sm text-gray-400">
                    <i class="fa-solid fa-inbox text-2xl mb-2 block text-gray-300"></i>
                    {{ search ? `Tidak ada widget cocok dengan "${search}".` : 'Belum ada widget.' }}
                  </td>
                </tr>
                <tr
                  v-for="(widget, index) in filteredWidgets"
                  :key="widget.id"
                  :data-id="widget.id"
                  class="hover:bg-gray-50 transition-colors"
                  :class="{ 'bg-indigo-50/60': form.id === widget.id }"
                >
                  <!-- Drag handle -->
                  <td class="py-3.5 pl-4 pr-2">
                    <div class="drag-handle flex items-center justify-center w-7 h-7 rounded hover:bg-gray-200 transition-colors cursor-grab active:cursor-grabbing" title="Seret untuk mengubah posisi">
                      <i class="fa-solid fa-grip-vertical text-gray-400 text-sm"></i>
                    </div>
                  </td>
                  <td class="py-3.5 px-3 text-sm text-gray-500 font-medium">{{ index + 1 }}</td>
                  <td class="py-3.5 px-4 text-sm font-semibold text-gray-900">{{ widget.name }}</td>
                  <td class="hidden md:table-cell py-3.5 px-4 text-sm text-gray-500">
                    <code class="px-1.5 py-0.5 text-xs bg-gray-100 text-gray-600 rounded font-mono">{{ widget.component }}</code>
                  </td>
                  <td class="hidden lg:table-cell py-3.5 px-4 text-sm text-gray-500 max-w-xs">
                    <span class="line-clamp-1">{{ widget.description || '—' }}</span>
                  </td>
                  <td class="py-3.5 px-4 text-center">
                    <span class="inline-flex items-center justify-center w-7 h-7 text-xs font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-full">
                      {{ widget.order }}
                    </span>
                  </td>
                  <td class="py-3.5 px-4">
                    <span
                      class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full border"
                      :class="widget.is_active
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-gray-100 text-gray-500 border-gray-200'"
                    >
                      <i :class="widget.is_active ? 'fa-solid fa-circle text-[8px]' : 'fa-regular fa-circle text-[8px]'"></i>
                      {{ widget.is_active ? 'Aktif' : 'Nonaktif' }}
                    </span>
                  </td>
                  <td class="py-3.5 pl-3 pr-6">
                    <div class="flex items-center justify-end gap-1.5">
                      <button
                        @click="editWidget(widget)"
                        class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 transition-colors"
                        :class="{ 'ring-2 ring-indigo-400': form.id === widget.id }"
                        title="Edit"
                      >
                        <i class="fa-solid fa-pen-to-square"></i>
                        <span class="hidden sm:inline">Edit</span>
                      </button>
                      <button
                        @click="toggleWidgetStatus(widget)"
                        class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium border rounded-md transition-colors"
                        :class="widget.is_active
                          ? 'text-amber-600 bg-amber-50 border-amber-200 hover:bg-amber-100'
                          : 'text-emerald-600 bg-emerald-50 border-emerald-200 hover:bg-emerald-100'"
                        :title="widget.is_active ? 'Nonaktifkan' : 'Aktifkan'"
                      >
                        <i :class="widget.is_active ? 'fa-solid fa-toggle-on' : 'fa-solid fa-toggle-off'"></i>
                        <span class="hidden sm:inline">{{ widget.is_active ? 'Nonaktifkan' : 'Aktifkan' }}</span>
                      </button>
                      <button
                        @click="deleteWidget(widget)"
                        class="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors"
                        title="Hapus"
                      >
                        <i class="fa-solid fa-trash"></i>
                        <span class="hidden sm:inline">Hapus</span>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Form Card -->
        <div ref="formCard" class="bg-white border border-gray-200 shadow-sm rounded-xl">
          <div class="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-t-xl">
            <h2 class="flex items-center gap-2 text-lg font-bold text-white">
              <i :class="isEditMode ? 'fa-solid fa-pen-to-square' : 'fa-solid fa-plus'"></i>
              {{ isEditMode ? 'Edit Widget' : 'Tambah Widget' }}
            </h2>
          </div>

          <form @submit.prevent="submitForm" class="p-6 space-y-5">
            <!-- Nama + Komponen -->
            <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label for="name" class="block mb-1.5 text-sm font-medium text-gray-700">Nama Widget</label>
                <input
                  v-model="form.name"
                  type="text"
                  id="name"
                  placeholder="Masukkan nama widget"
                  required
                  class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                />
                <p v-if="form.errors.name" class="mt-1.5 text-xs text-red-600">{{ form.errors.name }}</p>
              </div>
              <div>
                <label for="component" class="block mb-1.5 text-sm font-medium text-gray-700">Komponen</label>
                <select
                  v-model="form.component"
                  id="component"
                  required
                  class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                >
                  <option value="">Pilih Komponen</option>
                  <option
                    v-for="(displayName, componentPath) in props.availableComponents"
                    :key="displayName"
                    :value="displayName"
                  >
                    {{ componentPath }}
                  </option>
                </select>
                <p v-if="form.errors.component" class="mt-1.5 text-xs text-red-600">{{ form.errors.component }}</p>
              </div>
            </div>

            <!-- Urutan + Aktif -->
            <div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label for="order" class="block mb-1.5 text-sm font-medium text-gray-700">Urutan</label>
                <input
                  v-model="form.order"
                  type="number"
                  id="order"
                  min="0"
                  placeholder="0"
                  class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
                />
                <p v-if="form.errors.order" class="mt-1.5 text-xs text-red-600">{{ form.errors.order }}</p>
              </div>
              <div class="flex items-end pb-1">
                <label class="flex items-center gap-3 cursor-pointer select-none">
                  <div class="relative">
                    <input type="checkbox" v-model="form.is_active" class="sr-only peer" />
                    <div class="w-10 h-5 bg-gray-200 rounded-full peer peer-checked:bg-indigo-600 transition-colors"></div>
                    <div class="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow peer-checked:translate-x-5 transition-transform"></div>
                  </div>
                  <span class="text-sm font-medium text-gray-700">
                    {{ form.is_active ? 'Aktif' : 'Nonaktif' }}
                  </span>
                </label>
              </div>
            </div>

            <!-- Deskripsi -->
            <div>
              <label for="description" class="block mb-1.5 text-sm font-medium text-gray-700">Deskripsi <span class="text-gray-400 font-normal">(opsional)</span></label>
              <textarea
                v-model="form.description"
                id="description"
                rows="3"
                placeholder="Masukkan deskripsi widget"
                class="block w-full px-3 py-2.5 text-sm text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 hover:border-gray-400 transition-colors"
              ></textarea>
              <p v-if="form.errors.description" class="mt-1.5 text-xs text-red-600">{{ form.errors.description }}</p>
            </div>

            <!-- Actions -->
            <div class="flex items-center justify-end gap-3 pt-2 border-t border-gray-100">
              <button
                v-if="isEditMode"
                type="button"
                @click="cancelEdit"
                class="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <i class="fa-solid fa-times mr-1.5"></i>Batal
              </button>
              <button
                type="submit"
                :disabled="form.processing"
                class="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <i class="fa-solid fa-floppy-disk"></i>
                {{ form.processing ? 'Menyimpan...' : isEditMode ? 'Simpan Perubahan' : 'Simpan' }}
              </button>
            </div>
          </form>
        </div>

      </div>
    </template>
  </Container>
</template>

<script setup>
import { Head, useForm, router } from "@inertiajs/vue3";
import { nextTick, onMounted, ref, computed, inject } from "vue";
import Sortable from "sortablejs";
import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import Swal from "sweetalert2";

const route = inject('route');

const props = defineProps({
  widgets: Array,
  availableComponents: Object,
});

const search = ref("");
const isEditMode = ref(false);
const widgetList = ref(null);
const formCard = ref(null);

// Normalize: controller passes WidgetResource::collection → { data: [...] }
const allWidgets = computed(() => {
  if (Array.isArray(props.widgets)) return props.widgets;
  if (Array.isArray(props.widgets?.data)) return props.widgets.data;
  return [];
});

const filteredWidgets = computed(() => {
  const q = search.value.toLowerCase().trim();
  if (!q) return allWidgets.value;
  return allWidgets.value.filter(
    (w) =>
      w.name.toLowerCase().includes(q) ||
      w.component.toLowerCase().includes(q) ||
      (w.description && w.description.toLowerCase().includes(q))
  );
});

const form = useForm({
  id: null,
  name: "",
  component: "",
  description: "",
  order: 0,
  is_active: true,
  config: {},
});

const reorderForm = useForm({ ordered_ids: [] });

onMounted(() => {
  nextTick(() => {
    if (!widgetList.value) return;
    Sortable.create(widgetList.value, {
      handle: ".drag-handle",
      animation: 150,
      ghostClass: "bg-indigo-50",
      chosenClass: "bg-indigo-100",
      dragClass: "sortable-drag shadow-xl",
      forceFallback: true,
      fallbackClass: "sortable-fallback",
      fallbackOnBody: true,
      fallbackTolerance: 5,
      emptyInsertThreshold: 15,
      delay: 150,
      delayOnTouchOnly: true,
      onStart: (evt) => {
        evt.item.classList.add("scale-105");
        widgetList.value.classList.add("dragging-active");
      },
      onChange: (evt) => {
        const rows = widgetList.value.querySelectorAll("tr");
        rows.forEach((r) => r.classList.remove("drop-target-above", "drop-target-below"));
        if (evt.related) {
          const ri = evt.newIndex > evt.oldIndex ? evt.newIndex : evt.newIndex - 1;
          if (ri >= 0 && ri < rows.length) rows[ri].classList.add("drop-target-below");
        }
      },
      onEnd: (evt) => {
        evt.item.classList.remove("scale-105");
        widgetList.value.classList.remove("dragging-active");
        widgetList.value.querySelectorAll("tr").forEach((r) =>
          r.classList.remove("drop-target-above", "drop-target-below")
        );
        reorderForm.ordered_ids = Array.from(widgetList.value.children).map((el) =>
          parseInt(el.getAttribute("data-id"))
        );
        reorderForm.post(route("widgets.reorder"), {
          preserveScroll: true,
          onError: () => Swal.fire({ title: "Gagal!", text: "Gagal memperbarui urutan widget.", icon: "error" }),
        });
      },
    });
  });
});

const submitForm = () => {
  const method = isEditMode.value ? "put" : "post";
  const routeUrl = isEditMode.value ? route("widgets.update", form.id) : route("widgets.store");

  form[method](routeUrl, {
    onSuccess: () => {
      isEditMode.value = false;
      form.reset();
      form.id = null;
    },
    onError: () => Swal.fire({ title: "Gagal!", text: "Terjadi kesalahan saat menyimpan widget.", icon: "error" }),
  });
};

const editWidget = (widget) => {
  isEditMode.value = true;
  form.id          = widget.id;
  form.name        = widget.name;
  form.component   = widget.component;
  form.description = widget.description || "";
  form.order       = widget.order;
  form.is_active   = widget.is_active;
  form.config      = widget.config || {};

  nextTick(() => {
    formCard.value?.scrollIntoView({ behavior: "smooth", block: "start" });
  });
};

const cancelEdit = () => {
  isEditMode.value = false;
  form.reset();
  form.id = null;
};

const deleteWidget = (widget) => {
  Swal.fire({
    title: "Apakah Anda yakin?",
    text: `Widget "${widget.name}" akan dihapus permanen!`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#dc2626",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Ya, hapus!",
    cancelButtonText: "Batal",
  }).then((result) => {
    if (!result.isConfirmed) return;
    router.delete(route("widgets.destroy", widget.id), {
      onError: () => Swal.fire({ title: "Gagal!", text: "Gagal menghapus widget.", icon: "error" }),
    });
  });
};

const toggleWidgetStatus = (widget) => {
  router.put(route("widgets.toggle-status", widget.id), {}, {
    onError: () => Swal.fire({ title: "Gagal!", text: "Gagal mengubah status widget.", icon: "error" }),
  });
};
</script>

<style scoped>
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>

<style>
.sortable-drag {
  background-color: #f3f4f6 !important;
  box-shadow: 0 10px 15px -3px rgba(0,0,0,.1), 0 4px 6px -2px rgba(0,0,0,.05) !important;
  opacity: 0.85;
  border: 1px dashed #6366f1;
}
.sortable-fallback {
  opacity: 0.85;
  transform: scale(1.03);
  background-color: #f3f4f6 !important;
  box-shadow: 0 10px 20px -3px rgba(0,0,0,.15) !important;
  border: 2px solid #6366f1 !important;
  pointer-events: none;
}
.scale-105 { transform: scale(1.02); transition: transform .15s; }
.dragging-active tr { position: relative; }
.dragging-active tr::after,
.dragging-active tr::before {
  content: "";
  display: block;
  height: 3px;
  background-color: transparent;
  position: absolute;
  left: 0; right: 0;
  z-index: 5;
  transition: background-color .15s;
}
.dragging-active tr::after { bottom: -2px; }
.dragging-active tr::before { top: -2px; }
.drop-target-above::before { background-color: #6366f1 !important; }
.drop-target-below::after  { background-color: #6366f1 !important; }
</style>
